import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { Gem, HelpCircle, History, CircleDot } from 'lucide-react'
import { toast } from 'react-hot-toast'
import useDoubleStore from '../store/useDoubleStore'
import DoubleWheel from '../components/DoubleWheel'
import DoubleBetModal from '../components/DoubleBetModal'
import DoubleBetsFeed from '../components/DoubleBetsFeed'
import DoubleDesktopBetPanel from '../components/DoubleDesktopBetPanel'
import DoublePerformanceChart from '../components/DoublePerformanceChart'
import HelpModal from '../../../components/HelpModal'
import useAudioManager from '../../../hooks/useAudioManager'

const getMyBetKey = (bet, index) => {
  const stableId = bet?.id ?? bet?.bet_id ?? bet?.created_at ?? null

  if (stableId !== null && stableId !== undefined && stableId !== '') {
    return `my-bet-${stableId}`
  }

  const color = String(bet?.color || 'unknown')
  const sum = Number(bet?.sum || 0)
  const crystal = Number(bet?.crystal || 0)
  const itemsCount = Array.isArray(bet?.bet_items) ? bet.bet_items.length : 0
  const chatId = String(bet?.chat_id || 'anon')

  return `my-bet-fallback-${chatId}-${color}-${sum}-${crystal}-${itemsCount}-${index}`
}

const getStatusPollDelay = (status) => {
  if (status === 'raffle') return 300
  if (status === 'finished') return 300
  if (status === 'result') return 300
  return 1000
}

const TOTAL_SUM_ANIMATION_DURATION = 1000
const COLOR_MULTIPLIER = { red: 2, black: 2, green: 14 }

const getRecentRoundCircleClass = (color) => {
  const normalizedColor = String(color || '').toLowerCase()

  if (normalizedColor === 'red') {
    return 'bg-red-500 border-red-300/70 text-red-50'
  }

  if (normalizedColor === 'black') {
    return 'bg-slate-900 border-slate-300/60 text-slate-100'
  }

  return 'bg-emerald-500 border-emerald-300/70 text-emerald-50'
}

// Анимированный счётчик общей суммы по цвету: проигрыш → 0, победа → x2/x14
const AnimatedColorTotal = ({ value, shouldAnimate, isWinner, multiplier }) => {
  const [displayValue, setDisplayValue] = useState(Number(value || 0))
  const rafRef = useRef(null)
  const startTsRef = useRef(null)

  useEffect(() => {
    if (rafRef.current) {
      cancelAnimationFrame(rafRef.current)
      rafRef.current = null
    }

    const safeValue = Number(value || 0)

    if (!shouldAnimate) {
      setDisplayValue(safeValue)
      return
    }

    const from = safeValue
    const to = isWinner ? Math.round(safeValue * multiplier) : 0
    startTsRef.current = null

    const tick = (ts) => {
      if (!startTsRef.current) startTsRef.current = ts
      const elapsed = ts - startTsRef.current
      const progress = Math.min(elapsed / TOTAL_SUM_ANIMATION_DURATION, 1)
      const eased = 1 - (1 - progress) * (1 - progress)
      const current = Math.round(from + (to - from) * eased)
      setDisplayValue(current)

      if (progress < 1) {
        rafRef.current = requestAnimationFrame(tick)
      }
    }

    rafRef.current = requestAnimationFrame(tick)

    return () => {
      if (rafRef.current) {
        cancelAnimationFrame(rafRef.current)
      }
    }
  }, [value, shouldAnimate, isWinner, multiplier])

  return <>{Math.max(0, displayValue).toLocaleString()}</>
}

const Double = () => {
  const navigate = useNavigate()
  const { t } = useTranslation()
  const { playSound } = useAudioManager({
    hover: 'hover',
    userJoinBattle: 'userJoinBattle',
    select: 'select'
  })
  const {
    activeRound,
    inventory,
    balance,
    isBetting,
    loadActiveRound,
    loadBetForm,
    loadHistoryFast,
    placeBet
  } = useDoubleStore()

  const [isBetModalOpen, setIsBetModalOpen] = useState(false)
  const [isHelpOpen, setIsHelpOpen] = useState(false)
  const [selectedColor, setSelectedColor] = useState(null)
  const [countdownMs, setCountdownMs] = useState(Math.max(0, Number(activeRound?.second_to_raffle || 0) * 1000))
  const [recentRounds, setRecentRounds] = useState([])
  const [myBets, setMyBets] = useState([])

  const pollTimeoutRef = useRef(null)
  const timerIntervalRef = useRef(null)
  const abortControllerRef = useRef(null)
  const isPollingActiveRef = useRef(false)
  const countdownTargetTsRef = useRef(null)
  const lastCountdownSyncRef = useRef({ roundId: null, status: null })
  const winToastRoundIdRef = useRef(null)
  const betDataRefreshPromiseRef = useRef(null)
  const betDataIntervalRef = useRef(null)
  const recentHistoryPromiseRef = useRef(null)
  const recentHistoryRefreshTimeoutRef = useRef(null)
  const lastRecentHistoryRoundIdRef = useRef(null)
  const shouldPlayBetAcceptedSoundRef = useRef(false)
  const prevMyBetKeysRef = useRef(new Set())
  const [isBetDataReady, setIsBetDataReady] = useState(false)

  const roundId = activeRound?.id
  const isRoundReady = Boolean(roundId)
  const roundStatus = activeRound?.status || null
  const bets = useMemo(() => (Array.isArray(activeRound?.bets) ? activeRound.bets : []), [activeRound?.bets])
  const sums = {
    red: Number(activeRound?.sum_red || 0),
    black: Number(activeRound?.sum_black || 0),
    green: Number(activeRound?.sum_green || 0)
  }
  const myColorSums = useMemo(() => {
    return bets.reduce(
      (acc, bet) => {
        if (!bet?.your_bet) return acc

        const color = bet?.color
        if (!['red', 'black', 'green'].includes(color)) return acc

        acc[color] += Number(bet?.sum || 0)
        return acc
      },
      { red: 0, black: 0, green: 0 }
    )
  }, [bets])
  const stats100 = activeRound?.stats_100 || {}
  const modalInventory = useMemo(() => (Array.isArray(inventory) ? inventory : []), [inventory])

  const isBetLocked = !isRoundReady || ['raffle', 'result', 'finished'].includes(roundStatus)
  const isResultPhase = ['result', 'finished'].includes(roundStatus)
  const winColor = activeRound?.win_color || null
  const currentBalance = Number(balance || 0)
  const shouldAnimateTotals = isResultPhase && Boolean(winColor)

  const handleStopPolling = () => {
    isPollingActiveRef.current = false

    if (pollTimeoutRef.current) {
      clearTimeout(pollTimeoutRef.current)
      pollTimeoutRef.current = null
    }

    if (abortControllerRef.current) {
      abortControllerRef.current.abort()
      abortControllerRef.current = null
    }
  }

  const startPolling = useCallback(async () => {
    if (!isPollingActiveRef.current) return

    const controller = new AbortController()
    abortControllerRef.current = controller

    try {
      const round = await loadActiveRound(controller.signal)
      if (!isPollingActiveRef.current) return

      const nextDelay = getStatusPollDelay(round?.status)
      pollTimeoutRef.current = setTimeout(startPolling, nextDelay)
    } catch (error) {
      const isAbortError = error?.name === 'AbortError' || controller.signal.aborted
      if (!isPollingActiveRef.current || isAbortError) return

      pollTimeoutRef.current = setTimeout(startPolling, 1500)
    } finally {
      if (abortControllerRef.current === controller) {
        abortControllerRef.current = null
      }
    }
  }, [loadActiveRound])

  useEffect(() => {
    isPollingActiveRef.current = true
    startPolling()

    return () => {
      handleStopPolling()
    }
  }, [startPolling])

  useEffect(() => {
    if (!activeRound?.id) return
    setIsBetModalOpen(false)
  }, [activeRound?.id])

  useEffect(() => {
    const nextRoundId = activeRound?.id || null
    const nextStatus = activeRound?.status || null
    const nextMilliseconds = Math.max(0, Number(activeRound?.second_to_raffle || 0) * 1000)
    const prevSync = lastCountdownSyncRef.current

    const isNewRound = prevSync.roundId !== nextRoundId
    const enteredBetStatus = !['new', 'started'].includes(prevSync.status) && ['new', 'started'].includes(nextStatus)

    if ((isNewRound || enteredBetStatus) && ['new', 'started'].includes(nextStatus)) {
      countdownTargetTsRef.current = Date.now() + nextMilliseconds
      setCountdownMs(nextMilliseconds)
    }

    if (!['new', 'started'].includes(nextStatus)) {
      countdownTargetTsRef.current = null
    }

    lastCountdownSyncRef.current = {
      roundId: nextRoundId,
      status: nextStatus
    }
  }, [activeRound?.id, activeRound?.status, activeRound?.second_to_raffle])

  useEffect(() => {
    if (timerIntervalRef.current) {
      clearInterval(timerIntervalRef.current)
      timerIntervalRef.current = null
    }

    if (!['new', 'started'].includes(roundStatus)) return

    timerIntervalRef.current = setInterval(() => {
      if (!countdownTargetTsRef.current) return

      const remainingMs = Math.max(0, countdownTargetTsRef.current - Date.now())
      setCountdownMs(remainingMs)

      if (remainingMs <= 0 && timerIntervalRef.current) {
        clearInterval(timerIntervalRef.current)
        timerIntervalRef.current = null
      }
    }, 33)

    return () => {
      if (timerIntervalRef.current) {
        clearInterval(timerIntervalRef.current)
        timerIntervalRef.current = null
      }
    }
  }, [roundStatus])

  const refreshBetData = useCallback(async ({ showError = false } = {}) => {
    if (betDataRefreshPromiseRef.current) {
      return betDataRefreshPromiseRef.current
    }

    betDataRefreshPromiseRef.current = (async () => {
      try {
        await loadBetForm()
        setIsBetDataReady(true)

        return true
      } catch (error) {
        if (showError) {
          toast.error(t('double.toasts.betFormError', 'Не удалось загрузить форму ставки'))
        }
        return false
      } finally {
        betDataRefreshPromiseRef.current = null
      }
    })()

    return betDataRefreshPromiseRef.current
  }, [loadBetForm, t])

  const refreshRecentRounds = useCallback(async () => {
    if (recentHistoryPromiseRef.current) {
      return recentHistoryPromiseRef.current
    }

    recentHistoryPromiseRef.current = (async () => {
      try {
        const { rounds, myBets: betsData } = await loadHistoryFast()

        setMyBets(betsData)

        const normalizedRounds = rounds
          .filter((round) => round?.win_sector !== null && typeof round?.win_sector !== 'undefined')
          .slice(0, 10)
          .map((round) => ({
            id: String(round?.id || ''),
            winColor: String(round?.win_color || '').toLowerCase(),
            winSector: String(round?.win_sector ?? '—')
          }))

        setRecentRounds(normalizedRounds)
      } catch (error) {
        // Ошибку истории не показываем как toast, чтобы не мешать игровому процессу
      } finally {
        recentHistoryPromiseRef.current = null
      }
    })()

    return recentHistoryPromiseRef.current
  }, [loadHistoryFast])

  useEffect(() => {
    if (!roundId || !isResultPhase) return
    if (winToastRoundIdRef.current === roundId) return

    const hasMyWinningBet = bets.some((bet) => bet?.your_bet && bet?.win === true)
    if (!hasMyWinningBet) return

    winToastRoundIdRef.current = roundId
    toast.success(t('double.toasts.youWon', 'Вы выиграли!'))
  }, [bets, isResultPhase, roundId, t])

  useEffect(() => {
    const myBetKeys = new Set(
      bets
        .map((bet, index) => ({ bet, index }))
        .filter(({ bet }) => Boolean(bet?.your_bet))
        .map(({ bet, index }) => getMyBetKey(bet, index))
    )

    const prevMyBetKeys = prevMyBetKeysRef.current
    const hasNewMyBet = Array.from(myBetKeys).some((key) => !prevMyBetKeys.has(key))

    if (shouldPlayBetAcceptedSoundRef.current && hasNewMyBet) {
      playSound('userJoinBattle')
      shouldPlayBetAcceptedSoundRef.current = false
    }

    prevMyBetKeysRef.current = myBetKeys
  }, [bets, playSound])

  useEffect(() => {
    refreshBetData()

    if (betDataIntervalRef.current) {
      clearInterval(betDataIntervalRef.current)
      betDataIntervalRef.current = null
    }

    betDataIntervalRef.current = setInterval(() => {
      refreshBetData()
    }, 5000)

    return () => {
      if (betDataIntervalRef.current) {
        clearInterval(betDataIntervalRef.current)
        betDataIntervalRef.current = null
      }
    }
  }, [refreshBetData])

  useEffect(() => {
    if (recentHistoryRefreshTimeoutRef.current) {
      clearTimeout(recentHistoryRefreshTimeoutRef.current)
      recentHistoryRefreshTimeoutRef.current = null
    }

    const nextRoundId = roundId || null
    const prevRoundId = lastRecentHistoryRoundIdRef.current
    const isFirstHistorySync = prevRoundId === null
    const isNewRound = prevRoundId !== null && prevRoundId !== nextRoundId

    lastRecentHistoryRoundIdRef.current = nextRoundId

    if (isNewRound) {
      recentHistoryRefreshTimeoutRef.current = setTimeout(() => {
        refreshRecentRounds()
        recentHistoryRefreshTimeoutRef.current = null
      }, 2000)

      return () => {
        if (recentHistoryRefreshTimeoutRef.current) {
          clearTimeout(recentHistoryRefreshTimeoutRef.current)
          recentHistoryRefreshTimeoutRef.current = null
        }
      }
    }

    if (isFirstHistorySync || !nextRoundId) {
      refreshRecentRounds()
    }

    return () => {
      if (recentHistoryRefreshTimeoutRef.current) {
        clearTimeout(recentHistoryRefreshTimeoutRef.current)
        recentHistoryRefreshTimeoutRef.current = null
      }
    }
  }, [refreshRecentRounds, roundId])

  const handleOpenBetModal = async (color) => {
    if (isBetLocked) return

    playSound('select')

    const prepared = isBetDataReady ? true : await refreshBetData({ showError: true })
    if (!prepared) return

    setSelectedColor(color)
    setIsBetModalOpen(true)
  }

  const handleSubmitBet = async ({ color, crystal, items }) => {
    if (!roundId) {
      toast.error(t('double.toasts.roundUnavailable', 'Раунд недоступен'))
      return false
    }

    try {
      await placeBet({ roundId, color, crystal, items })
      shouldPlayBetAcceptedSoundRef.current = true

      await Promise.allSettled([
        loadActiveRound(),
        refreshBetData()
      ])

      setIsBetModalOpen(false)
      return true
    } catch (error) {
      if (error.message === 'not enough balance') {
        toast.error(t('double.toasts.notEnoughBalance', 'Недостаточно кристаллов'))
        return false
      }
      if (error.message === 'error sum') {
        toast.error(t('double.toasts.minBet', 'Минимальная ставка 100 кристаллов'))
        return false
      }
      if (error.message === 'error max bet') {
        toast.error(t('double.toasts.maxBet', 'Максимальная сумма ставки 100 000'))
        return false
      }
      toast.error(t('double.toasts.betError', 'Не удалось отправить ставку'))
      return false
    }
  }

  const statsRows = useMemo(() => {
    const total = Number(stats100.red || 0) + Number(stats100.black || 0) + Number(stats100.green || 0)
    const safeTotal = total > 0 ? total : 1

    return [
      { key: 'red', label: t('double.colors.red', 'Красный'), value: Number(stats100.red || 0), colorClass: 'bg-red-500' },
      { key: 'black', label: t('double.colors.black', 'Чёрный'), value: Number(stats100.black || 0), colorClass: 'bg-slate-500' },
      { key: 'green', label: t('double.colors.green', 'Зелёный'), value: Number(stats100.green || 0), colorClass: 'bg-emerald-400' }
    ].map((item) => ({
      ...item,
      percent: (item.value / safeTotal) * 100
    }))
  }, [stats100, t])

  const statsTotalRounds = useMemo(() => {
    return statsRows.reduce((acc, row) => acc + Number(row.value || 0), 0)
  }, [statsRows])

  const redShare = statsRows.find((row) => row.key === 'red')?.percent || 0
  const blackShare = statsRows.find((row) => row.key === 'black')?.percent || 0
  const ringGradient = `conic-gradient(
    rgba(239,68,68,0.98) 0% ${redShare}%,
    rgba(100,116,139,0.98) ${redShare}% ${redShare + blackShare}%,
    rgba(16,185,129,0.98) ${redShare + blackShare}% 100%
  )`

  const helpSteps = useMemo(() => ([
    t('double.help.step1', 'Выберите цвет ставки: красный или чёрный дают x2, зелёный даёт x14.'),
    t('double.help.step2', 'Добавьте кристаллы и/или предметы, минимальная ставка — 100, а максимальная — 100 000.'),
    t('double.help.step3', 'Дождитесь розыгрыша: колесо определит сектор и выигравший цвет раунда.')
  ]), [t])

  const handleColorButtonHover = () => {
    playSound('hover')
  }

  return (
    <div className="px-4 py-6 pb-16 lg:px-6 lg:py-2 lg:pb-8 overflow-x-hidden">
      <div className="mx-auto max-w-7xl space-y-6 lg:space-y-8">
        <div className="flex items-center justify-center">
          <div className="flex items-center gap-2">
            <h1 className="flex items-center gap-2 text-2xl lg:text-4xl font-bold text-accent-primary">
              <CircleDot className="w-6 h-6 shrink-0 lg:w-8 lg:h-8" />
              <span>{t('double.title', 'Double')}</span>
            </h1>
            <button
              type="button"
              onClick={() => setIsHelpOpen(true)}
              className="p-1.5 rounded-full bg-dark-secondary/50 hover:bg-dark-secondary transition-colors"
              aria-label={t('double.help.button', 'Справка по режиму Double')}
            >
              <HelpCircle className="w-5 h-5 text-text-secondary" />
            </button>
          </div>
        </div>

        <div className="grid gap-6 lg:grid-cols-[minmax(0,1fr)_350px]">
          <div className="space-y-6">
            {isRoundReady ? (
              <DoubleWheel
                roundId={roundId}
                status={roundStatus}
                prevSector={activeRound?.prev_round?.win_sector}
                winSector={activeRound?.win_sector}
                winColor={activeRound?.win_color}
                countdownMs={countdownMs}
                raffleSeconds={activeRound?.second_to_finish_raffle}
              />
            ) : (
              <div className="rounded-2xl p-6">
                <div className="mx-auto aspect-square w-full max-w-[340px] rounded-full border border-white/10 bg-dark-primary/70 flex items-center justify-center">
                  <div className="flex flex-col items-center gap-3 text-center">
                    <div className="h-8 w-8 rounded-full border-2 border-accent-primary/70 border-t-transparent animate-spin" />
                  </div>
                </div>
              </div>
            )}

            <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
              <button
                disabled={isBetLocked}
                onClick={() => handleOpenBetModal('red')}
                onMouseEnter={handleColorButtonHover}
                className={`relative overflow-hidden rounded-2xl border px-4 py-3 text-left transition-all duration-300 ${
                  isResultPhase && winColor === 'red'
                    ? 'border-red-300/80 bg-gradient-to-br from-red-500/70 via-rose-500/50 to-red-900/60 shadow-[0_0_30px_rgba(239,68,68,0.5)] animate-pulse ring-2 ring-red-400/60'
                    : 'border-red-300/45 bg-gradient-to-br from-red-500/50 via-rose-500/30 to-red-900/45 shadow-[0_8px_24px_rgba(239,68,68,0.28)] hover:-translate-y-0.5 hover:border-red-200/70 hover:shadow-[0_14px_34px_rgba(239,68,68,0.45)] active:translate-y-0'
                } ${isBetLocked && !(isResultPhase && winColor === 'red') ? 'opacity-45 transform-none !shadow-none' : ''}`}
              >
                <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(circle_at_20%_15%,rgba(255,255,255,0.24),transparent_45%)]" />
                <div className="relative flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <p className="text-red-50 font-semibold tracking-wide">{t('double.colors.red', 'Красное')} ×2</p>
                    {myColorSums.red > 0 && (
                      <p className="mt-1 text-xs font-semibold text-red-200/95">
                        {t('double.myBet', 'Моя ставка')}: {myColorSums.red.toLocaleString()} <Gem className="inline w-3 h-3" />
                      </p>
                    )}
                  </div>
                  <p className="shrink-0 text-red-100 text-sm inline-flex items-center gap-1 font-medium">
                    <AnimatedColorTotal
                      value={sums.red}
                      shouldAnimate={shouldAnimateTotals}
                      isWinner={winColor === 'red'}
                      multiplier={COLOR_MULTIPLIER.red}
                    /> <Gem className="inline w-3.5 h-3.5" />
                  </p>
                </div>
              </button>

              <button
                disabled={isBetLocked}
                onClick={() => handleOpenBetModal('black')}
                onMouseEnter={handleColorButtonHover}
                className={`relative overflow-hidden rounded-2xl border px-4 py-3 text-left transition-all duration-300 ${
                  isResultPhase && winColor === 'black'
                    ? 'border-slate-200/70 bg-gradient-to-br from-slate-600/90 via-slate-800/95 to-black/95 shadow-[0_0_30px_rgba(148,163,184,0.35)] animate-pulse ring-2 ring-slate-300/50'
                    : 'border-slate-300/35 bg-gradient-to-br from-slate-700/70 via-slate-900/80 to-black/90 shadow-[0_8px_24px_rgba(2,6,23,0.55)] hover:-translate-y-0.5 hover:border-slate-200/70 hover:shadow-[0_14px_34px_rgba(2,6,23,0.75)] active:translate-y-0'
                } ${isBetLocked && !(isResultPhase && winColor === 'black') ? 'opacity-45 transform-none !shadow-none' : ''}`}
              >
                <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(circle_at_20%_15%,rgba(255,255,255,0.14),transparent_45%)]" />
                <div className="relative flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <p className="text-slate-100 font-semibold tracking-wide">{t('double.colors.black', 'Чёрное')} ×2</p>
                    {myColorSums.black > 0 && (
                      <p className="mt-1 text-xs font-semibold text-slate-100/95">
                        {t('double.myBet', 'Моя ставка')}: {myColorSums.black.toLocaleString()} <Gem className="inline w-3 h-3" />
                      </p>
                    )}
                  </div>
                  <p className="shrink-0 text-slate-200 text-sm inline-flex items-center gap-1 font-medium">
                    <AnimatedColorTotal
                      value={sums.black}
                      shouldAnimate={shouldAnimateTotals}
                      isWinner={winColor === 'black'}
                      multiplier={COLOR_MULTIPLIER.black}
                    /> <Gem className="inline w-3.5 h-3.5" />
                  </p>
                </div>
              </button>

              <button
                disabled={isBetLocked}
                onClick={() => handleOpenBetModal('green')}
                onMouseEnter={handleColorButtonHover}
                className={`relative overflow-hidden rounded-2xl border px-4 py-3 text-left transition-all duration-300 ${
                  isResultPhase && winColor === 'green'
                    ? 'border-emerald-300/80 bg-gradient-to-br from-emerald-400/75 via-emerald-500/55 to-emerald-900/65 shadow-[0_0_30px_rgba(16,185,129,0.5)] animate-pulse ring-2 ring-emerald-400/60'
                    : 'border-emerald-300/45 bg-gradient-to-br from-emerald-400/55 via-emerald-500/35 to-emerald-900/50 shadow-[0_8px_24px_rgba(16,185,129,0.28)] hover:-translate-y-0.5 hover:border-emerald-200/70 hover:shadow-[0_14px_34px_rgba(16,185,129,0.45)] active:translate-y-0'
                } ${isBetLocked && !(isResultPhase && winColor === 'green') ? 'opacity-45 transform-none !shadow-none' : ''}`}
              >
                <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(circle_at_20%_15%,rgba(255,255,255,0.24),transparent_45%)]" />
                <div className="relative flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <p className="text-emerald-50 font-semibold tracking-wide">{t('double.colors.green', 'Зелёное')} ×14</p>
                    {myColorSums.green > 0 && (
                      <p className="mt-1 text-xs font-semibold text-emerald-200/95">
                        {t('double.myBet', 'Моя ставка')}: {myColorSums.green.toLocaleString()} <Gem className="inline w-3 h-3" />
                      </p>
                    )}
                  </div>
                  <p className="shrink-0 text-emerald-100 text-sm inline-flex items-center gap-1 font-medium">
                    <AnimatedColorTotal
                      value={sums.green}
                      shouldAnimate={shouldAnimateTotals}
                      isWinner={winColor === 'green'}
                      multiplier={COLOR_MULTIPLIER.green}
                    /> <Gem className="inline w-3.5 h-3.5" />
                  </p>
                </div>
              </button>
            </div>

            <DoubleBetsFeed bets={bets} roundStatus={roundStatus} winColor={activeRound?.win_color} />
          </div>

          <div className="space-y-6 lg:sticky lg:top-0 h-fit">
            <div className="hidden lg:block">
              <DoubleDesktopBetPanel
                isDisabled={isBetLocked}
                isLoading={isBetting}
                balance={currentBalance}
                inventory={modalInventory}
                onSubmit={handleSubmitBet}
              />
            </div>

            <DoublePerformanceChart myBets={myBets} />

            <div className="rounded-2xl border border-white/10 bg-surface/50 p-4">
              <h3 className="text-white font-semibold mb-3 hidden">{t('double.stats100.title', '100 последних раундов')}</h3>
              <div className="flex items-center gap-4">
                <div className="relative h-24 w-24 shrink-0 rounded-full p-[7px] shadow-[0_0_24px_rgba(16,185,129,0.18)]" style={{ background: ringGradient }}>
                  <div className="h-full w-full rounded-full bg-dark-primary/95 border border-white/10 flex flex-col items-center justify-center">
                    <span className="text-base font-bold text-white leading-none">{statsTotalRounds}</span>
                    <span className="text-[9px] uppercase tracking-wide text-text-secondary mt-1">{t('double.stats100.rounds', 'раундов')}</span>
                  </div>
                </div>

                <div className="min-w-0 flex-1 space-y-2">
                  {statsRows.map((row) => (
                    <div key={`ring-legend-${row.key}`} className="flex items-center justify-between text-xs">
                      <span className="inline-flex items-center gap-1.5 text-text-secondary min-w-0">
                        <span
                          className={`h-2.5 w-2.5 rounded-full shrink-0 ring-1 ring-white/15 ${row.key === 'red' ? 'bg-red-500' : row.key === 'black' ? 'bg-slate-500' : 'bg-emerald-400'}`}
                        />
                        <span className="truncate">{row.label}</span>
                      </span>

                      <span className="text-white font-medium tabular-nums">
                        {row.percent.toFixed(0)}%
                      </span>
                    </div>
                  ))}
                </div>
              </div>

              <div className="mt-4 border-t border-white/10 pt-3">
                {recentRounds.length > 0 ? (
                  <div className="flex flex-wrap items-center justify-center gap-1.5">
                    {recentRounds.slice(0, 6).map((round) => (
                      <button
                        type="button"
                        key={round.id || `${round.winColor}-${round.winSector}`}
                        onClick={() => {
                          if (!round.id) return
                          navigate(`/double/history/${round.id}`)
                        }}
                        disabled={!round.id}
                        className={`inline-flex h-8 w-8 items-center justify-center rounded-full border text-xs font-bold tabular-nums  hover:scale-100 disabled:cursor-default disabled:hover:scale-100 ${getRecentRoundCircleClass(round.winColor)}`}
                        aria-label={t('double.history.openRound', 'Открыть раунд {{roundId}}', { roundId: round.id || round.winSector })}
                        title={`${round.winColor} #${round.winSector}`}
                      >
                        {round.winSector}
                      </button>
                    ))}

                    <button
                      type="button"
                      onClick={() => navigate('/double/history')}
                      className="inline-flex h-8 min-w-8 items-center justify-center rounded-full border border-white/20 bg-dark-primary/60 px-1 text-xs font-bold text-text-secondary transition-colors hover:text-white"
                      aria-label={t('double.history.moreRounds', 'Перейти в историю раундов')}
                      title={t('double.history.moreRounds', 'Перейти в историю раундов')}
                    >
                      ...
                    </button>
                  </div>
                ) : (
                  <p className="text-xs text-text-secondary hidden">
                    {t('double.history.empty', 'История пока недоступна')}
                  </p>
                )}
              </div>
            </div>

            <button
                type="button"
                onClick={() => navigate('/double/history')}
                className="mt-4 inline-flex items-center justify-center gap-2 px-3 py-1.5 rounded-full bg-surface text-text-secondary hover:text-white border border-white/10 transition-colors"
              >
                <History className="w-4 h-4" />
                <span className="text-sm">{t('double.buttons.history', 'История')}</span>
            </button>

          </div>
        </div>
      </div>

      <HelpModal
        isOpen={isHelpOpen}
        onClose={() => setIsHelpOpen(false)}
        title={t('double.help.title', 'Как работает Double')}
        steps={helpSteps}
        highlightLastStep={false}
      />

      <DoubleBetModal
        isOpen={isBetModalOpen}
        color={selectedColor}
        isLoading={isBetting}
        balance={currentBalance}
        inventory={modalInventory}
        onClose={() => setIsBetModalOpen(false)}
        onSubmit={handleSubmitBet}
      />
    </div>
  )
}

export default Double
